import React, { memo, useCallback, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { NavItem, NavIcon } from './types';
import { Icon } from '../../ui/icon';

/** Renders a navigation icon (FontAwesome class string, IconDefinition, or React component) */
function renderNavIcon(
  icon: NavIcon,
  className?: string,
  style?: React.CSSProperties
) {
  if (!icon) return null;
  if (typeof icon === 'string') {
    // FontAwesome class string - use Icon component
    return <Icon icon={icon} className={className} style={style} />;
  }
  // Check if it's an IconDefinition (has iconName and prefix properties)
  if (typeof icon === 'object' && 'iconName' in icon && 'prefix' in icon) {
    return (
      <FontAwesomeIcon
        icon={icon as IconDefinition}
        className={className}
        style={style}
      />
    );
  }
  if (typeof icon === 'function') {
    const IconComponent = icon;
    // Function components may not accept style prop, so only pass className
    return <IconComponent className={className} />;
  }
  return null;
}

interface AppSidebarItemProps {
  item: NavItem;
  isActive: boolean;
  isCollapsed: boolean;
  className?: string;
}

function AppSidebarItemComponent({
  item,
  isActive,
  isCollapsed,
  className,
}: AppSidebarItemProps) {
  const isHighlight = item.variant === 'highlight';
  const isBlueText = item.className?.includes('sidebar-item--blue-text');
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isMounted, setIsMounted] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);

  // Only mount on client side to avoid SSR issues with portals
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const getItemElement = (): HTMLElement | null => {
    return buttonRef.current || anchorRef.current || linkRef.current;
  };

  const updateTooltipPosition = useCallback(() => {
    const element = getItemElement();
    if (!element) return;
    const rect = element.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 15,
    });
  }, []);

  useEffect(() => {
    if (isHovered) {
      updateTooltipPosition();
      const handleResize = () => updateTooltipPosition();
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleResize, true);
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleResize, true);
      };
    }
  }, [isHovered, updateTooltipPosition]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    // Small delay to ensure DOM is updated
    setTimeout(updateTooltipPosition, 0);
  }, [updateTooltipPosition]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const renderIcon = () => {
    if (!item.icon) return null;

    // Don't apply filters for blue-text items - let CSS handle the color
    if (isBlueText) {
      return renderNavIcon(item.icon);
    }

    // For string icons (FontAwesome), use CSS class for color instead of filter
    // Filters can break FontAwesome icon rendering
    if (typeof item.icon === 'string') {
      return renderNavIcon(item.icon);
    }

    // For function components, render directly
    return renderNavIcon(item.icon);
  };

  const linkContent = (
    <>
      <span className="sidebar-item__icon">{renderIcon()}</span>
      <span className="sidebar-item__label">{item.label}</span>
    </>
  );

  const tooltipContent =
    item.description && typeof document !== 'undefined' ? (
      <div
        className="sidebar-tooltip"
        style={{
          position: 'fixed',
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          transform: 'translateY(-50%)',
          opacity: isHovered ? 1 : 0,
          visibility: isHovered ? 'visible' : 'hidden',
          pointerEvents: 'none',
          zIndex: 50,
        }}
      >
        <div className="sidebar-tooltip__description">{item.description}</div>
      </div>
    ) : null;

  const itemClass = [
    'sidebar-item',
    isCollapsed && 'sidebar-item--collapsed',
    isHighlight && 'sidebar-item--highlight',
    !isHighlight && isActive && 'sidebar-item--active',
    item.className,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const clickHandler = useCallback(
    (e: React.MouseEvent) => {
      if (item.onClick) {
        e.preventDefault();
        item.onClick();
      }
    },
    [item.onClick]
  );

  /** Renders a clickable button element for items with onClick handlers */
  const renderClickableButton = () => (
    <button
      type="button"
      ref={buttonRef}
      onClick={clickHandler}
      className={itemClass}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      id={item.id}
    >
      {linkContent}
    </button>
  );

  const renderTooltip = () => {
    if (!item.description || !isMounted || typeof document === 'undefined') {
      return null;
    }
    return createPortal(tooltipContent, document.body);
  };

  if (item.onClick) {
    return (
      <>
        {renderClickableButton()}
        {renderTooltip()}
      </>
    );
  }

  if (item.isExternal) {
    return (
      <>
        <a
          href={item.href}
          ref={anchorRef}
          className={itemClass}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          id={item.id}
        >
          {linkContent}
        </a>
        {renderTooltip()}
      </>
    );
  }

  // Use Link with our custom active state handling
  return (
    <>
      <Link
        to={item.href}
        ref={linkRef}
        className={itemClass}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        id={item.id}
      >
        {linkContent}
      </Link>
      {renderTooltip()}
    </>
  );
}

export const AppSidebarItem = memo(
  AppSidebarItemComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.item.href === nextProps.item.href &&
      prevProps.item.description === nextProps.item.description &&
      prevProps.item.onClick === nextProps.item.onClick &&
      prevProps.item.variant === nextProps.item.variant &&
      prevProps.item.id === nextProps.item.id &&
      prevProps.isActive === nextProps.isActive &&
      prevProps.isCollapsed === nextProps.isCollapsed &&
      prevProps.className === nextProps.className
    );
  }
);
